import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useForm, useFormState } from 'react-final-form';
import { Checkbox } from 'ncoded-component-library';
import { updateFromAllCheckboxValue } from '../../checkboxUtils';
import { useTranslation } from 'react-i18next';
import {
  BulkAvailabilityBuyGroup,
  BulkAvailabilityBuySideMembersForm,
  BulkFormAvailabilityMember,
} from 'router/subrouters/DataRoom/models/DataRoomBulk';
import DurationDaysField from 'components/DurationDaysField';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  getGlobalFileExpirationDateInDays,
  getIsCheckedAndNotMixed,
} from '../../utils';
import useBusEvent from 'hooks/useBusEvent';
import bus, { StandardEventTypes } from 'modules/bus';

import './AllBuySidesGroupAccess.styles.scss';

type AllBuySidesGroupAccessProps = {
  className?: string;
};

const AllBuySidesGroupAccess: React.FC<AllBuySidesGroupAccessProps> = (
  props,
) => {
  const { className } = props;

  const classes = classNames('all-buy-sides-group-access', className);

  const {
    dataRoom: { accessConfirmation },
  } = useContext(DataRoomContext);

  const { batch, change } = useForm();
  const { values } = useFormState();

  const { t } = useTranslation();

  const { fileExpirationDateInDays, buySideGroups = [] } = values || {};

  const { numOfGroups, numOfBuyers } = useMemo(() => {
    const numOfGroups = buySideGroups?.length;

    const numOfBuyers = buySideGroups?.reduce(
      (acc: number, el: BulkAvailabilityBuyGroup) =>
        acc + el.dataRoomMembers.length,
      0,
    );

    return {
      numOfGroups,
      numOfBuyers,
    };
  }, [buySideGroups]);

  const hasMoreBuySideGroups = useMemo(() => numOfGroups > 1, [numOfGroups]);

  const isViewNotCheckedForAll = useMemo(
    () =>
      buySideGroups.every(({ dataRoomMembers }: BulkAvailabilityBuyGroup) =>
        dataRoomMembers.every(
          (member: BulkFormAvailabilityMember) => !member.view.checked,
        ),
      ),
    [buySideGroups],
  );

  const isDownloadNotCheckedForAll = useMemo(
    () =>
      buySideGroups.every(({ dataRoomMembers }: BulkAvailabilityBuyGroup) =>
        dataRoomMembers.every(
          (member: BulkFormAvailabilityMember) => !member.download.checked,
        ),
      ),
    [buySideGroups],
  );

  const isViewMixedForAll = useMemo(
    () =>
      !isViewNotCheckedForAll &&
      !buySideGroups.every(({ dataRoomMembers }: BulkAvailabilityBuyGroup) =>
        dataRoomMembers.every((member: BulkFormAvailabilityMember) =>
          getIsCheckedAndNotMixed(member.view),
        ),
      ),
    [buySideGroups, isViewNotCheckedForAll],
  );

  const isDownloadMixedForAll = useMemo(
    () =>
      !isDownloadNotCheckedForAll &&
      !buySideGroups.every(({ dataRoomMembers }: BulkAvailabilityBuyGroup) =>
        dataRoomMembers.every(
          (member: BulkFormAvailabilityMember) =>
            member.download.checked && !member.download.isMixed,
        ),
      ),
    [buySideGroups, isDownloadNotCheckedForAll],
  );

  const showFileAccessCycle = useMemo(
    () =>
      accessConfirmation &&
      buySideGroups
        .flatMap(
          ({ dataRoomMembers }: BulkAvailabilityBuyGroup) => dataRoomMembers,
        )
        .find((member: BulkFormAvailabilityMember) =>
          getIsCheckedAndNotMixed(member.view),
        ),
    [accessConfirmation, buySideGroups],
  );

  const checkNearestAccessCycle = useCallback(() => {
    const leastFileExpirationDateInDays = getGlobalFileExpirationDateInDays(
      buySideGroups as BulkAvailabilityBuySideMembersForm[],
    );

    change('fileExpirationDateInDays', leastFileExpirationDateInDays);
  }, [buySideGroups, change]);

  useBusEvent(StandardEventTypes.ACCESS_CYCLE_EVENT, checkNearestAccessCycle);

  const broadcastAccessCycleEvent = useCallback(
    () =>
      setTimeout(
        () => bus.broadcastEvent(StandardEventTypes.ACCESS_CYCLE_EVENT),
        0,
      ),
    [],
  );

  const updateViewForAll = useCallback(() => {
    batch(() =>
      buySideGroups.forEach(
        ({ dataRoomMembers }: BulkAvailabilityBuyGroup, groupIndex: number) =>
          dataRoomMembers.forEach(
            (member: BulkFormAvailabilityMember, index: number) =>
              updateFromAllCheckboxValue({
                name: `buySideGroups[${groupIndex}].dataRoomMembers[${index}]`,
                isDownload: false,
                checked: isViewNotCheckedForAll || isViewMixedForAll,
                memberCurrentState: member,
                onChange: change,
              }),
          ),
      ),
    );
    broadcastAccessCycleEvent();
  }, [
    buySideGroups,
    isViewNotCheckedForAll,
    isViewMixedForAll,
    change,
    batch,
    broadcastAccessCycleEvent,
  ]);

  const updateDownloadForAll = useCallback(() => {
    batch(() =>
      buySideGroups.forEach(
        ({ dataRoomMembers }: BulkAvailabilityBuyGroup, groupIndex: number) =>
          dataRoomMembers.forEach(
            (member: BulkFormAvailabilityMember, index: number) =>
              updateFromAllCheckboxValue({
                name: `buySideGroups[${groupIndex}].dataRoomMembers[${index}]`,
                isDownload: true,
                checked: isDownloadNotCheckedForAll || isDownloadMixedForAll,
                memberCurrentState: member,
                onChange: change,
              }),
          ),
      ),
    );
    broadcastAccessCycleEvent();
  }, [
    buySideGroups,
    isDownloadNotCheckedForAll,
    isDownloadMixedForAll,
    change,
    batch,
    broadcastAccessCycleEvent,
  ]);

  const updateFileAccessForAll = useCallback(
    () =>
      buySideGroups.forEach(
        ({ dataRoomMembers }: BulkAvailabilityBuyGroup, groupIndex: number) =>
          dataRoomMembers.forEach((_, index: number) => {
            if (fileExpirationDateInDays)
              change(
                `buySideGroups[${groupIndex}].dataRoomMembers[${index}].fileExpirationDateInDays`,
                fileExpirationDateInDays,
              );
          }),
      ),
    [buySideGroups, fileExpirationDateInDays, change],
  );

  if (!hasMoreBuySideGroups) return null;

  return (
    <div className={classes}>
      <div className="info">
        <p>
          <span>{t('numOfMembers', { count: numOfBuyers })}</span>
          <span>{t('numOfGroups', { count: numOfGroups })}</span>
        </p>
        <p className="note">{t('overrideAccessForAllBuyers')}</p>
      </div>

      <div className="actions">
        <div className="checkboxes">
          <Checkbox
            label={t('view')}
            checked={!isViewNotCheckedForAll}
            className={classNames({ mixed: isViewMixedForAll })}
            onChange={updateViewForAll}
          />

          <Checkbox
            label={t('download')}
            checked={!isDownloadNotCheckedForAll}
            className={classNames({ mixed: isDownloadMixedForAll })}
            onChange={updateDownloadForAll}
          />
        </div>

        {showFileAccessCycle && (
          <DurationDaysField
            name="fileExpirationDateInDays"
            label={t('nearestAccessCycleEnds')}
            required
            copyForMinValidation="minimumAccessCycleInDays"
            copyForMaxValidation="maximumAccessCycleInDays"
            customOnBlur={updateFileAccessForAll}
          />
        )}
      </div>
    </div>
  );
};

export default AllBuySidesGroupAccess;
